import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withFormik } from 'formik';
import Yup from 'yup';
import { TextFieldInput, Message, CountrySelect } from 'components';
import { signUp } from 'services/auth';
import { getMessage } from 'common/getMessage';
import { submitActionEventToGtm, notifyToSlack } from 'common/gtmScripts';
import { submitActionEvent } from 'common/crmScripts';
import { onSignUpSuccess } from './actions';

import { APP_NAME, PAGES } from 'common/const';

const DEFAULT_SCHEMA = Yup.object().shape({
    firstName: Yup.string().required('First Name is required'),
    lastName: Yup.string().required('Last Name is required'),
    email: Yup.string()
        .email('Invalid email address')
        .required('Email is required'),
    confirmEmail: Yup.string()
        .required('Confirm email is required')
        .oneOf([Yup.ref('email'), null], 'Emails do not match'),
    country: Yup.string().required('Country/ Region is required'),
    companyName: Yup.string().required('Company name is required'),
    street: Yup.string().required('Address is required'),
    city: Yup.string().required('City is required')
});

const formikEnhancer = withFormik({
    validationSchema: DEFAULT_SCHEMA,
    mapPropsToValues: ({ user, data: invite }) => ({
        ...user,
        email: invite && invite.invitee
    }),
    handleSubmit: (
        payload,
        { setSubmitting, setStatus, props: { data, onSignUpSuccess } }
    ) => {
        signUp({
            city: payload.city,
            companyName: payload.companyName,
            country: payload.country,
            email: payload.email,
            firstName: payload.firstName,
            lastName: payload.lastName,
            state: payload.state,
            street: payload.street,
            vatNumber: payload.vatNumber,
            serviceType: APP_NAME,
            token: data && data.accessToken
        })
            .then(res => {
                submitActionEventToGtm('Account created');
                submitActionEvent('signup', payload);
                notifyToSlack({
                    userEmail: payload.email,
                    status: 'did',
                    stepName: 'Sign up'
                });

                onSignUpSuccess(Object.assign(res, { email: payload.email }));
                setSubmitting(false);
            })
            .catch(e => {
                setSubmitting(false);
                setStatus({
                    error: getMessage({
                        token: e.message,
                        httpCode: e.code || e.status
                    })
                });

                notifyToSlack({
                    userEmail: payload.email,
                    status: 'failed',
                    stepName: 'Sign up',
                    errorCode: e.message
                });
            });
    },
    displayName: 'SignUpForm'
});

const SignUpForm = props => {
    const {
        values,
        touched,
        errors,
        isValid,
        handleChange,
        handleBlur,
        setFieldTouched,
        setFieldValue,
        handleSubmit,
        isSubmitting,
        status,
        header
    } = props;

    const message = status && status.error && (
        <Message type="danger" message={status.error} />
    );

    return (
        <div className="sign-up-form">
            {message}
            {!!header && header}
            <form onSubmit={handleSubmit} autoComplete="off">
                <div className="row">
                    <div className="col-md-6">
                        <TextFieldInput
                            id="firstName"
                            type="text"
                            label="First name"
                            placeholder=""
                            error={touched.firstName && errors.firstName}
                            value={values.firstName}
                            required
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    </div>
                    <div className="col-md-6">
                        <TextFieldInput
                            id="lastName"
                            type="text"
                            label="Last name"
                            placeholder=""
                            error={touched.lastName && errors.lastName}
                            value={values.lastName}
                            required
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <TextFieldInput
                            id="email"
                            type="email"
                            label="Email"
                            placeholder=""
                            error={touched.email && errors.email}
                            value={values.email}
                            required
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    </div>
                    <div className="col-md-6">
                        <TextFieldInput
                            id="confirmEmail"
                            type="email"
                            label="Confirm email"
                            placeholder=""
                            error={touched.confirmEmail && errors.confirmEmail}
                            value={values.confirmEmail}
                            required
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onPaste="off"
                            autoComplete="new-value"
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <TextFieldInput
                            id="companyName"
                            type="text"
                            label="Company name"
                            placeholder=""
                            error={touched.companyName && errors.companyName}
                            value={values.companyName}
                            required
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    </div>
                    <div className="col-md-6">
                        <CountrySelect
                            id="country"
                            label="Country/ Region"
                            value={values.country}
                            required
                            placeholder=""
                            error={touched.country && errors.country}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            setFieldTouched={setFieldTouched}
                            setFieldValue={setFieldValue}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <TextFieldInput
                            id="city"
                            type="text"
                            label="City"
                            placeholder=""
                            error={touched.city && errors.city}
                            value={values.city}
                            required
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    </div>
                    <div className="col-md-6">
                        <TextFieldInput
                            id="street"
                            type="text"
                            label="Address"
                            placeholder="Area and street address"
                            error={touched.street && errors.street}
                            value={values.street}
                            required
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <TextFieldInput
                            id="vatNumber"
                            type="text"
                            label="VAT number"
                            placeholder=""
                            value={values.vatNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    </div>
                    <div className="col-md-6">
                        <div className="note signup-note">
                            E.g: Your country code is SE and VAT number is
                            123456789000. VAT number format will be
                            SE123456789000.
                        </div>
                        <div className="read-more-link">
                            <a
                                href="http://ec.europa.eu/taxation_customs/vies/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Information on how to check the VAT number
                            </a>
                        </div>
                    </div>
                </div>

                <div className="row space read-more-link policy-consent">
                    <span>
                        By clicking Sign Up, you are indicating that you have
                        read and agree to the
                        <br />
                        <a
                            href={PAGES.LANDING + PAGES.TERMS}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Terms of Service
                        </a>{' '}
                        and{' '}
                        <a
                            href={PAGES.LANDING + PAGES.PRIVACY}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Privacy Policy
                        </a>
                        .
                    </span>
                </div>

                <div className="submit-btns">
                    <button
                        type="submit"
                        className={`btn btn-signup ${
                            isSubmitting ? 'loading' : ''
                        }`}
                        disabled={isSubmitting || !isValid}
                    >
                        Sign Up
                    </button>
                </div>
            </form>
        </div>
    );
};

const EnhancedForm = formikEnhancer(SignUpForm);

const mapDispatchToProps = (dispatch, ownProps) => {
    return bindActionCreators(
        {
            onSignUpSuccess
        },
        dispatch
    );
};

export default connect(null, mapDispatchToProps)(EnhancedForm);
